import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { CompanyFilter } from "../../../legacy/types/companyFilter";
import { useWorkspaceStore } from "../../../store/workspace/WorkspaceStore";
import { FILTERS_KEYS, FILTER_TYPE_MAP } from "./constants";
import { clearFiltersByKeys, clearOpportunityTypeFilterState } from "./utils";
import {
  hasFilterConflict,
  FilterConflict,
  getCurrentFilterTypes,
} from "../utils";
import { useMatchDataProcessingStore } from "../../../store/MatchDataProcessingStore";
import { useFilterConflicts } from "./useFilterConflicts";

export interface PendingFiltersState {
  filters: CompanyFilter | undefined;
  conflicts: FilterConflict[];
}

/**
 * Hook to manage additional filters state and actions
 */
const useFiltersContextValue = () => {
  const userFilters = useWorkspaceStore((state) => state.userFilters);
  const setUserFilters = useWorkspaceStore((state) => state.setUserFilters);
  const draft = useWorkspaceStore((state) => state.draft);
  const {
    shownConflictTypes,
    setShownConflictTypes,
    pendingFilters,
    setPendingFilters,
    clearGlobalFilterConflicts,
  } = useFilterConflicts(draft, userFilters);

  /**
   * This state stores additional workspace filters before they are saved to the workspace object
   */
  const [draftFilters, setDraftFiltersInternal] = useState<
    CompanyFilter | undefined
  >(userFilters);

  const setDraftFilters = useCallback(
    (
      filters:
        | CompanyFilter
        | undefined
        | ((prev: CompanyFilter | undefined) => CompanyFilter | undefined)
    ) => {
      setDraftFiltersInternal(filters);
    },
    []
  );

  // Check if uploaded accounts is being processed
  const uploadedAccountsProcessing = useMatchDataProcessingStore((state) =>
    draft?.id ? state.getProcessingFile(draft.id, "uploadedAccounts") : null
  );
  const stopProcessing = useMatchDataProcessingStore(
    (state) => state.stopProcessing
  );

  // Reset filter overrides when userFilters are cleared
  useEffect(() => {
    const isUserFiltersEmpty =
      !userFilters || Object.keys(userFilters).length === 0;

    if (isUserFiltersEmpty) {
      setDraftFiltersInternal((prev) => {
        const isPrevEmpty = !prev || Object.keys(prev).length === 0;
        return isPrevEmpty ? prev : {};
      });
    }
  }, [userFilters]);

  const hasAnyOverrides = useMemo(() => {
    if (pendingFilters || !draft || !userFilters) return false;
    const currentFilterTypes = getCurrentFilterTypes(userFilters);
    const conflicts = hasFilterConflict(draft, userFilters, currentFilterTypes);
    if (conflicts.length > 0) {
      setShownConflictTypes(new Set(conflicts.map((c) => c.type)));
    }
    return conflicts.length > 0;
  }, [draft, userFilters, pendingFilters, setShownConflictTypes]);

  useEffect(() => {
    if (userFilters) {
      setDraftFilters(userFilters);
    }
  }, [draft?.id, userFilters, setDraftFilters]);

  useEffect(() => {
    // Skip if user doesn't have a processed company match yet
    if (
      !userFilters?.companyMatch?.fileId ||
      !userFilters?.companyMatch?.lastMatched
    ) {
      return;
    }

    // Skip if draft already has the same processed match
    if (
      draftFilters?.companyMatch?.fileId ===
        userFilters?.companyMatch?.fileId &&
      draftFilters?.companyMatch?.lastMatched
    ) {
      return;
    }

    // Sync draft with user filters
    setDraftFiltersInternal((prev) => ({
      ...prev,
      companyMatch: userFilters.companyMatch,
    }));
  }, [
    userFilters?.companyMatch,
    draftFilters?.companyMatch?.fileId,
    draftFilters?.companyMatch?.lastMatched,
  ]);

  /**
   * Function to apply filter overrides after user confirmation
   */
  const applyPendingFiltersWithOverride = useCallback(() => {
    if (!pendingFilters) return;
    pendingFilters.conflicts.forEach((conflict) => {
      shownConflictTypes.add(conflict.type);
    });
    setUserFilters(pendingFilters.filters);
    setPendingFilters(null);
    clearGlobalFilterConflicts();
  }, [
    pendingFilters,
    setUserFilters,
    shownConflictTypes,
    clearGlobalFilterConflicts,
    setPendingFilters,
  ]);

  /**
   * Function that applies specific filters to the workspace context
   */
  const handleApplySpecificFilters = useCallback(
    (filtersToApply: CompanyFilter | undefined) => {
      const processedFilters = { ...filtersToApply };

      // For global presence, we need to ensure that the filter is applied correctly
      if (processedFilters.globalPresence) {
        const {
          subsidiaryLocations,
          hqCountryCodes,
          hqRegions,
          isMultiNational,
        } = processedFilters.globalPresence || {};

        const hasGlobalPresenceValues = [
          !!subsidiaryLocations,
          !!hqCountryCodes?.length,
          !!hqRegions?.length,
          !!isMultiNational,
        ].some(Boolean);

        if (!hasGlobalPresenceValues) {
          delete processedFilters.globalPresence;
        }
      }

      // Check for conflicts before applying filters
      const currentFilterTypes = getCurrentFilterTypes(processedFilters);
      const conflicts = hasFilterConflict(
        draft,
        processedFilters,
        currentFilterTypes
      );

      if (conflicts.length > 0) {
        // Store the pending filters and conflicts
        setPendingFilters({
          filters: processedFilters,
          conflicts,
        });

        return;
      }

      // No conflicts, apply filters directly
      setUserFilters(processedFilters);
    },
    [draft, setUserFilters, setPendingFilters]
  );

  /**
   * Function that applies the draftFilters to the workspace context
   */
  const handleApplyFilters = useCallback(() => {
    const filtersToApply = { ...draftFilters };
    handleApplySpecificFilters(filtersToApply);
  }, [draftFilters, handleApplySpecificFilters]);

  /**
   * Function to delete a single filter
   */
  const handleDeleteFilter = (filterKey: string) => {
    const newDraftFilters = clearFiltersByKeys([filterKey], draftFilters || {});

    // Update both draft and user filters
    setDraftFiltersInternal(newDraftFilters);
    setUserFilters(newDraftFilters);

    // Remove the filter type from shown conflicts when deleting
    if (FILTER_TYPE_MAP[filterKey]) {
      const conflictType = FILTER_TYPE_MAP[filterKey];
      setShownConflictTypes((prev) => {
        const newSet = new Set(prev);
        newSet.delete(conflictType);
        return newSet;
      });
    }
  };

  const cancelPendingFilters = useCallback(() => {
    if (!pendingFilters || !userFilters || !Object.keys(userFilters).length) {
      setPendingFilters(null);
      return;
    }
    const keysToDelete = pendingFilters.conflicts
      .map((conflict) => FILTER_TYPE_MAP[conflict.type])
      .filter(Boolean);

    const cleanedFilters = clearFiltersByKeys(keysToDelete, userFilters);

    const finalFilters = Object.keys(cleanedFilters).length
      ? cleanedFilters
      : undefined;
    setUserFilters(finalFilters);
    setDraftFilters(finalFilters);
    setPendingFilters(null);
  }, [
    pendingFilters,
    userFilters,
    setUserFilters,
    setPendingFilters,
    setDraftFilters,
  ]);

  const clearCompanyMatch = useCallback(() => {
    if (uploadedAccountsProcessing && draft?.id) {
      stopProcessing(draft?.id, "uploadedAccounts");
    }

    if (userFilters?.companyMatch?.fileId) {
      setUserFilters({
        ...userFilters,
        companyMatch: undefined,
      });
    }
  }, [
    userFilters,
    setUserFilters,
    uploadedAccountsProcessing,
    draft?.id,
    stopProcessing,
  ]);

  /**
   * Function to clear draft filters for modal and filter chips
   */
  const handleClearFilters = () => {
    clearCompanyMatch();
    setDraftFiltersInternal(undefined);
    setPendingFilters(null);
    setShownConflictTypes(new Set());
  };

  const handleCancelApplyFilters = () => {
    setDraftFiltersInternal(userFilters);
    setPendingFilters(null);
  };

  /**
   * Function to immediately clear invalid applied filters
   */
  const clearInvalidAppliedFilter = useCallback(
    (filterKey: string) => {
      if (filterKey === FILTERS_KEYS.opportunityType) {
        const newFilters = clearOpportunityTypeFilterState(userFilters);
        setUserFilters(newFilters);
        setDraftFiltersInternal(newFilters);
      }
      // Add other filter types as needed
    },
    [userFilters, setUserFilters]
  );

  return {
    draftFilters,
    setDraftFilters,
    handleApplyFilters,
    handleApplySpecificFilters,
    handleCancelApplyFilters,
    handleClearFilters,
    handleDeleteFilter,
    pendingFilters,
    setPendingFilters,
    hasAnyOverrides,
    applyPendingFiltersWithOverride,
    shownConflictTypes,
    setShownConflictTypes,
    cancelPendingFilters,
    clearInvalidAppliedFilter,
  };
};

type FiltersContextType = ReturnType<typeof useFiltersContextValue>;

const FiltersContext = createContext<FiltersContextType | undefined>(undefined);

export const FiltersProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const value = useFiltersContextValue();

  return (
    <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>
  );
};

export const useFiltersContext = () => {
  const context = useContext(FiltersContext);
  if (!context) {
    throw new Error("useFiltersContext must be used within a FiltersProvider");
  }
  return context;
};

export default FiltersProvider;
